import React from 'react';
import {
  ImageBackground,
  ImageSourcePropType,
  StyleSheet,
  View,
} from 'react-native';
import type { AnimatedRef } from 'react-native-reanimated';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import ExpressiveXLText from 'shared/components/vxl/typography/expressive/ExpressiveXLText';
import VaroHandMdText from 'shared/components/vxl/typography/expressive/varoHand/VaroHandMdText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import theme from 'shared/styles/theme';

export type ProductIntroScreenTextStyle = 'center' | 'flex-start' | 'flex-end';

export interface ProductIntroScreenProps {
  backgroundTexture?: ImageSourcePropType;
  bodyContent?: React.ReactNode;
  eyebrowText?: string;
  headlineText?: string;
  headlineTextStyle?: 'expressive' | 'title';
  primaryButton?: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'customAttributes'
    | 'componentAnalytics'
  >;
  secondaryButton?: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'customAttributes'
    | 'componentAnalytics'
  >;
  /** You can use this to "stick" an arbitrary component in the sticky area above the buttons. */
  stickyItem?: React.ReactNode;
  footerItem?: React.ReactNode;
  /** Visual can be a key for either an illustration or an animation */
  visual: IllustrationKeyType;
  visualAccessibilityLabel?: string;
  /** Use this to give some spacing to the top of the component when needed, typically to give space to fill behind the nav and status bar. */
  topPadding?: number;
  /** Use this to set the text alignment for the headline text. */
  headlineTextAlign?: ProductIntroScreenTextStyle;
  /** Pass an animatedRef to enable scroll-driven UI thread animations (e.g. nav bar transparency). */
  animatedRef?: AnimatedRef<Animated.ScrollView>;
}

const ProductIntroScreen = ({
  backgroundTexture,
  bodyContent,
  eyebrowText,
  headlineText,
  headlineTextStyle,
  primaryButton,
  secondaryButton,
  topPadding,
  visual,
  visualAccessibilityLabel,
  stickyItem,
  footerItem,
  headlineTextAlign,
  animatedRef,
}: ProductIntroScreenProps) => {
  const insets = useSafeAreaInsets();
  return (
    <View style={styles.container}>
      <Animated.ScrollView
        ref={animatedRef}
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContentContainer}
        stickyHeaderIndices={[0]}
      >
        <ImageBackground
          source={backgroundTexture}
          style={styles.imageBackground}
        >
          <View style={{ paddingTop: topPadding }}>
            <View
              style={styles.visualContainer}
              accessibilityLabel={visualAccessibilityLabel}
            >
              <Illustration illustration={visual} />
            </View>
          </View>
        </ImageBackground>
        <View
          style={[
            styles.stackInFrontOfHeader,
            styles.textContainer,
            headlineTextAlign && { alignItems: headlineTextAlign },
          ]}
        >
          {eyebrowText ? (
            <VaroHandMdText style={styles.eyebrowText}>
              {eyebrowText}
            </VaroHandMdText>
          ) : null}
          {headlineTextStyle === 'expressive' ? (
            <ExpressiveXLText style={styles.headlineText}>
              {headlineText}
            </ExpressiveXLText>
          ) : (
            <TitleLgText
              style={
                headlineTextAlign ? { alignItems: headlineTextAlign } : null
              }
            >
              {headlineText}
            </TitleLgText>
          )}
        </View>
        {bodyContent ? (
          <View style={[styles.stackInFrontOfHeader, styles.bodyContent]}>
            {bodyContent}
          </View>
        ) : null}
      </Animated.ScrollView>
      {footerItem ? <View testID="footerItem">{footerItem}</View> : null}
      <View
        style={[
          styles.footerContainer,
          {
            paddingBottom: insets.bottom + theme.spacing.m,
          },
        ]}
      >
        {stickyItem ? <View>{stickyItem}</View> : null}
        {primaryButton ? <Button {...primaryButton} /> : null}
        {secondaryButton ? (
          <Button type="SECONDARY" {...secondaryButton} />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContent: {
    backgroundColor: theme.color.background.primary,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
  },
  eyebrowText: {
    color: theme.color.expressive.varoPurple,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  headlineText: { textAlign: 'center' },
  imageBackground: {
    overflow: 'hidden',
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  stackInFrontOfHeader: {
    zIndex: 11, // stickyHeaderIndices applies a zIndex of 10, so using 11 to stack in front of it
  },
  textContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.s,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.xl,
    textAlign: 'center',
  },
  visualContainer: {
    alignSelf: 'center',
    height: 200,
    width: 200,
  },
});

export default ProductIntroScreen;
